import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { StatusBadge } from "../../../constants/statusConstants";
import { AuthContext } from "../../../contexts/AuthContext";
import { firebaseService } from "../../../services/api/firebaseService";
import { orderService } from "../../../services/api/orderService";
import CustomTable from "../components/CustomTable";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import OrderDetailModal from "./components/popup/OrderDetailPopup";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";

const OrderManagement = () => {
  const { token } = useContext(AuthContext);

  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [editData, setEditData] = useState({});

  const [invoiceFile, setInvoiceFile] = useState(null);
  const [originalInvoice, setOriginalInvoice] = useState("");

  const fetchOrders = async (search = "") => {
    setTableLoading(true);
    try {
      const response = await orderService.get_list_order(token, {
        page: currentPage,
        limit: 10,
        search_value: search,
      });
      setOrders(response.data || []);
      setPagination(
        response.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 },
      );
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      toast.error("Có lỗi xảy ra khi tải danh sách đơn hàng");
    } finally {
      setTableLoading(false);
    }
  };

  const { debouncedSearch, searchImmediately } = useDebouncedSearch(
    (search) => {
      setCurrentPage(1);
      fetchOrders(search);
    },
    500,
  );

  useEffect(() => {
    fetchOrders(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearchClick = (value = searchTerm) => searchImmediately(value);

  const handleViewDetail = async (orderId, item) => {
    setDetailLoading(true);
    setShowDetail(true);
    try {
      const id = typeof orderId === "object" ? orderId?.orderId : orderId;
      const response = await orderService.get_detail_order(token, id);

      setSelectedOrder(response.data || item);
      setEditData({
        status: response.data?.status || item?.status || "",
        invoice: response.data?.invoice || "",
        note: response.data?.note || "",
      });

      setOriginalInvoice(response.data?.invoice || "");
      setInvoiceFile(null);
    } catch (error) {
      console.error("Error fetching order detail:", error);
      if (item) {
        setSelectedOrder(item);
        setEditData({
          status: item?.status || "",
          invoice: "",
          note: "",
        });
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedOrder?.orderId) {
      toast.error("Không tìm thấy ID đơn hàng");
      return;
    }

    try {
      setUpdateLoading(true);
      let invoiceUrl = originalInvoice;

      if (invoiceFile) {
        if (originalInvoice?.startsWith("https://")) {
          await firebaseService.delete_file(token, originalInvoice);
        }

        const uploadRes = await firebaseService.upload_file(
          token,
          invoiceFile,
          "order-invoices",
        );

        if (!uploadRes?.url) {
          toast.error("Upload hóa đơn thất bại");
          return;
        }

        invoiceUrl = uploadRes.url;
      }

      await orderService.update(token, selectedOrder.orderId, {
        ...editData,
        invoice: invoiceUrl,
      });

      toast.success("Cập nhật đơn hàng thành công");
      setInvoiceFile(null);

      // Refresh detail after update
      setDetailLoading(true);
      try {
        const response = await orderService.get_detail_order(
          token,
          selectedOrder.orderId,
        );
        setSelectedOrder(response.data);
        setEditData({
          status: response.data?.status || "",
          invoice: response.data?.invoice || "",
          note: response.data?.note || "",
        });
        setOriginalInvoice(response.data?.invoice || "");
      } catch (error) {
        console.error("Error refreshing order detail:", error);
      } finally {
        setDetailLoading(false);
      }

      // Refresh list
      await fetchOrders(searchTerm);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Cập nhật đơn hàng thất bại");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSelectInvoiceFile = (file) => {
    if (!file) return;

    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Chỉ chấp nhận PDF / JPG / PNG");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File vượt quá 25MB");
      return;
    }

    setInvoiceFile(file);
    setEditData((prev) => ({
      ...prev,
      invoice: file.name,
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("vi-VN");
  };

  const columns = [
    {
      key: "orderCode",
      title: "Mã đơn hàng",
      align: "text-left",
      render: (value) => (
        <div className="flex items-center">
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      key: "customer",
      title: "Khách hàng",
      align: "text-left",
      render: (value) => (
        <div>
          <div className="text-gray-900">{value?.name}</div>
          <div className="text-gray-500 text-xs">{value?.email}</div>
        </div>
      ),
    },
    {
      key: "profile",
      title: "Nhãn hiệu",
      align: "text-left",
      render: (value) => <div className="text-gray-900">{value?.brand}</div>,
    },
    {
      key: "totalAmount",
      title: "Tổng tiền",
      align: "text-left",
      render: (value) => (
        <div className="font-semibold text-gray-900">
          {formatCurrency(value)}
        </div>
      ),
    },
    {
      key: "status",
      title: "Trạng thái",
      align: "text-left",
      render: (value) => (
        <StatusBadge status={value} className="min-w-[120px]" />
      ),
    },
    {
      key: "createdAt",
      title: "Ngày tạo",
      align: "text-left",
      render: (value) => (
        <span className="text-gray-500">{formatDate(value)}</span>
      ),
    },
  ];

  return (
    <div className="h-full bg-gray-50">
      <div className="h-full w-full mx-auto flex flex-col gap-4">
        <div className="shrink-0 px-1">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Quản lý đơn hàng
          </h1>
          <p className="text-gray-600">
            Quản lý và theo dõi tất cả đơn hàng của bạn
          </p>
        </div>

        <div className="shrink-0">
          <SearchAndFilterBar
            searchPlaceholder="Tìm kiếm theo mã đơn hàng, tên khách hàng hoặc email..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            onSearchClick={handleSearchClick}
            showDatePickers={false}
            showFilter={false}
            showAdditionBtn={false}
          />
        </div>

        <div className="flex-1 min-h-0">
          <CustomTable
            columns={columns}
            data={orders}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={pagination?.totalPages || 1}
            setView={handleViewDetail}
            setEdit={false}
            setDelete={false}
            setDuplicate={false}
            rowIdKey="orderId"
            loading={tableLoading}
            loadingText="Đang tải dữ liệu..."
          />
        </div>
      </div>

      <OrderDetailModal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        selectedOrder={selectedOrder}
        detailLoading={detailLoading}
        updateLoading={updateLoading}
        editData={editData}
        setEditData={setEditData}
        invoiceFile={invoiceFile}
        setInvoiceFile={setInvoiceFile}
        originalInvoice={originalInvoice}
        onUpdate={handleUpdate}
        onSelectInvoiceFile={handleSelectInvoiceFile}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default OrderManagement;
